import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Chip,
  Grid,
  Stack,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert
} from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { Edit, Visibility, Add, Refresh } from '@mui/icons-material';
import { Permit } from '../../types';
import { useAuth } from '../../hooks/useAuth';
import permitService from '../../services/permitService';

const PermitList: React.FC = () => {
  const [permits, setPermits] = useState<Permit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  const fetchPermits = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await permitService.getPermits();
      setPermits(response.results);
    } catch (err) {
      console.error('Error fetching permits:', err);
      setError(err instanceof Error ? err.message : 'Failed to load permits. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Only fetch permits if authenticated and not loading auth state
    if (isAuthenticated && !authLoading) {
      fetchPermits();
    } else if (!authLoading && !isAuthenticated) {
      // Clear permits if not authenticated
      setPermits([]);
      setLoading(false);
    }
  }, [isAuthenticated, authLoading]);

  const getStatusColor = (status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (status) {
      case 'planned': return 'default';
      case 'issued': return 'success';
      case 'suspended': return 'warning';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  if (authLoading || loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
        <Typography variant="body2" ml={2}>
          {authLoading ? 'Checking authentication...' : 'Loading permits...'}
        </Typography>
      </Box>
    );
  }

  if (!isAuthenticated) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <Alert severity="warning">
          Please log in to view permits.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header with Actions */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Permits
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchPermits}
            disabled={loading}
            title="Refresh permit list"
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<Add />}
            component={Link}
            to="/permits/new"
            title="Simple permits for General Work and Work Authority only"
          >
            General Work Permit
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<Add />}
            component={Link}
            to="/permits/access/new"
            title="StayLive Access Permit with intelligent isolation analysis"
          >
            StayLive Access Permit
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            component={Link}
            to="/permits/comprehensive"
            title="Complete permit system for Access Permits and Test Permits"
          >
            Access/Test Permit
          </Button>
          <Button
            variant="outlined"
            color="primary"
            component={Link}
            to="/permits/wizard"
            title="Guided permit selection based on work requirements"
          >
            Permit Wizard
          </Button>
        </Stack>
      </Box>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Permits Count */}
      <Typography variant="body1" color="text.secondary" mb={2}>
        {permits.length} permit{permits.length !== 1 ? 's' : ''} found
      </Typography>

      {/* Permits Table */}
      {permits.length === 0 ? (
        <Card>
          <CardContent>
            <Typography variant="h6" align="center" color="text.secondary">
              No permits found
            </Typography>
            <Typography variant="body2" align="center" color="text.secondary" mt={1}>
              Create your first permit using the Permit Wizard or Create Permit button above.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Permit Number</TableCell>
                <TableCell>Work Description</TableCell>
                <TableCell>Site</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Recipient</TableCell>
                <TableCell>Issuer</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Start Time</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {permits.map((permit) => (
                <TableRow key={permit.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {permit.permit_number || `PERMIT-${permit.id}`}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                      {permit.work_description}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {permit.site ? `${permit.site.name} (${permit.site.code})` : 'No site assigned'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {permit.work_location}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {permit.recipient?.first_name} {permit.recipient?.last_name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {permit.issuer?.first_name} {permit.issuer?.last_name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={permit.status?.toUpperCase() || 'UNKNOWN'}
                      color={getStatusColor(permit.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(permit.requested_start_time)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/permits/${permit.id}`)}
                        title="View Details"
                      >
                        <Visibility />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/permits/${permit.id}`)}
                        title="Edit Permit"
                      >
                        <Edit />
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default PermitList; 